import { useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { homePathFor } from '@/lib/home-path';
import { menuCatalog, primaryTabs, QUICK_MAX, quickActionsFor, visibleNavItems } from '@/layouts/mobile-nav';
import { useT } from '@/i18n/context';
import { ApiError, api } from '@/lib/api';
import { PREFS_KEY, usePreferences } from '@/lib/preferences';
import type { MyPreferences } from '@/lib/types';
import { Button, Modal, Note, useToast } from '@/components/ui';

const MOBILE = '(max-width: 900px)';
const subscribe = (cb: () => void) => {
  if (typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(MOBILE);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
};
const readMobile = () => typeof window.matchMedia === 'function' && window.matchMedia(MOBILE).matches;

/**
 * الإجراءاتُ السريعة (جولة الجوال، 26 سبتمبر 2026) — مكوّنٌ واحد من إعدادٍ واحد (`QUICK_ACTIONS`
 * في `nav.ts`) مصفّى بالصلاحية. **ويخصّصها كلُّ شخصٍ بنفسه** (27 سبتمبر 2026): يختار من فهرس صفاته
 * ويرتّب، واختيارُه في الخادم (`/me/preferences`) فيتبعه على كل أجهزته. وبلا اختيارٍ يبقى الافتراض:
 * فهرسُ صفته الأولى بلا ما في الشريط السفلي على الجوال.
 */
export function QuickActions() {
  const { me, roles } = useAuth();
  const t = useT();
  const mobile = useSyncExternalStore(subscribe, readMobile, () => false);
  const prefs = usePreferences();
  const [editing, setEditing] = useState(false);
  if (!me || me.student) return null;
  const home = homePathFor(roles);
  const exclude = new Set<string>([home]);
  if (mobile) {
    for (const t of primaryTabs(visibleNavItems({ roles, studentKind: null }), home)) exclude.add(t.to);
  }
  const chosen = prefs.data?.quickActions ?? null;
  // ما يُختار منه: كلُّ بنود قائمته لا فهرسٌ محدود (13 أكتوبر 2026)
  const catalog = menuCatalog(roles, (id) => t(id as Parameters<typeof t>[0]));
  const actions = quickActionsFor(roles, exclude, chosen, catalog);
  if (catalog.length === 0) return null;
  return (
    <>
      <nav className="quick-actions" aria-label="إجراءات سريعة" data-tour="quick">
        {actions.map((a) => (
          <Link key={a.to} to={a.to} className="quick-action">
            <a.icon aria-hidden />
            <span>{a.labelAr}</span>
          </Link>
        ))}
        <button type="button" className="quick-action quick-action--edit" onClick={() => setEditing(true)} aria-label="خصّص الاختصارات" title="خصّص الاختصارات">
          <Pencil aria-hidden />
          <span className="quick-action__edit-text">خصّص</span>
        </button>
      </nav>
      {editing && (
        <CustomizeDialog
          catalog={catalog}
          initial={chosen ?? actions.map((a) => a.to)}
          defaults={quickActionsFor(roles, exclude, null).map((a) => a.to)}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

/**
 * نافذةُ التخصيص: كلُّ اختصارات صفاته بخانة اختيار، وترتيبٌ بأزرار ▲▼ (لا بالسحب وحده — لوحةُ
 * المفاتيح وقارئُ الشاشة لا يسحبان)، وحدٌّ أقصى اثنا عشر، و«أعد الافتراضي» يحذف التفضيل فيعود الافتراض.
 */
function CustomizeDialog({ catalog, initial, defaults, onClose }: { catalog: ReturnType<typeof menuCatalog>; initial: string[]; defaults: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [order, setOrder] = useState<string[]>(() => [...initial.filter((k) => catalog.some((a) => a.to === k)), ...catalog.map((a) => a.to).filter((k) => !initial.includes(k))]);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initial));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    // بسياق المؤسسة الحالية: صفاتُ المعلّم والمختصّ تُشتقّ منه، وبلا سياقٍ لا يملك فهرسَ صفته
    mutationFn: (quickActions: string[] | null) => api.put<MyPreferences>('/me/preferences', { quickActions }),
    onSuccess: (p) => { qc.setQueryData(PREFS_KEY, p); toast.push('success', 'حُفظت اختصاراتك — تظهر هكذا على كل أجهزتك.'); onClose(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'تعذّر الحفظ.'),
  });
  const move = (i: number, d: -1 | 1) => setOrder((o) => { const n = [...o]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const toggle = (k: string) => setPicked((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const selected = order.filter((k) => picked.has(k));
  const reason = selected.length > QUICK_MAX ? `اختر ${QUICK_MAX} على الأكثر — اخترتَ ${selected.length}.` : undefined;
  const label = (k: string) => catalog.find((a) => a.to === k)?.labelAr ?? k;
  return (
    <Modal title="خصّص الاختصارات" description={`اختر ما تريد من كل بنود قائمتك (حتى ${QUICK_MAX}) ورتّبها — تُحفظ في حسابك فتتبعك على جوالك وحاسوبك.`} onClose={onClose}
      footer={(
        <>
          <Button variant="primary" loading={save.isPending} disabled={!!reason} disabledReason={reason} onClick={() => { setError(null); save.mutate(selected); }}>احفظ</Button>
          <Button variant="ghost" onClick={() => { setError(null); save.mutate(null); }} disabled={save.isPending}>أعد الافتراضي</Button>
        </>
      )}>
      <div className="stack gap-3">
        {error && <Note kind="danger">{error}</Note>}
        <p className="caption">الافتراضي: {defaults.map(label).join('، ') || '—'}.</p>
        <ol className="qa-edit">
          {order.map((k, i) => {
            const a = catalog.find((x) => x.to === k)!;
            const on = picked.has(k);
            return (
              <li key={k} className={`qa-edit__row${on ? ' qa-edit__row--on' : ''}`}>
                <label className="checkbox grow">
                  <input type="checkbox" checked={on} onChange={() => toggle(k)} />
                  <a.icon size={18} aria-hidden />
                  <span>{a.labelAr}</span>
                </label>
                <button type="button" className="icon-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`انقل «${a.labelAr}» إلى الأعلى`}><ArrowUp size={16} aria-hidden /></button>
                <button type="button" className="icon-btn" onClick={() => move(i, 1)} disabled={i === order.length - 1} aria-label={`انقل «${a.labelAr}» إلى الأسفل`}><ArrowDown size={16} aria-hidden /></button>
              </li>
            );
          })}
        </ol>
        <p className="caption" aria-live="polite">المختار: {selected.length} من {QUICK_MAX}.</p>
      </div>
    </Modal>
  );
}
